// src/components/LayerPanel/PanelHeader.jsx
//
// Sticky panel header. Three display states:
//
//   1. No operating AOI selected
//      A single yellow nudge: "Select an operating AOI to focus your
//      workspace." No "Active AOI" line, no AOI dropdown, no zoom
//      buttons — without a workbench there's nothing focus-within-
//      workbench to display.
//
//   2. Operating AOI set, no user AOI focus
//      Primary line: orange dot + operating AOI name + zoomTo to its
//      bounds. Secondary line: muted "No focus selected."
//
//   3. Operating AOI set + user AOI focus
//      Primary: orange dot + operating AOI name + zoomTo.
//      Secondary: blue dot + user AOI name (clickable to open the AOI
//      picker dropdown) + zoomTo to AOI bounds.
//
// Operating AOI = the workbench (useOperatingAoiContext, set via
// TopNav). User AOI = useAoiStore.selectedAoiId, the LayerPanel's
// per-workbench focus. The AOI dropdown opens on the secondary line
// and is positioned absolutely within this relative header.

import { useState, useRef, useEffect } from "react";
import { ChevronDown, Hexagon, Check, LocateFixed } from "lucide-react";
import { useAoiStore } from "../../stores/useAoiStore";
import { useMapActions } from "../../stores/useMapActions";
import { useOperatingAoiContext } from "../../stores/useOperatingAoiContext";
import { aoiBoundsFlat } from "../../utils/aoiBounds";

// Operating-AOI accent — same warm orange as the AOI ring on the map.
const OPERATING_AOI_COLOR = "#c2651a";

function cx(...c) { return c.filter(Boolean).join(" "); }

export default function PanelHeader() {
  const aois = useAoiStore((s) => s.aois);
  const selectedAoiId = useAoiStore((s) => s.selectedAoiId);
  const setSelectedAoiForMap = useAoiStore((s) => s.setSelectedAoiForMap);
  const selectedAoi = aois.find((a) => a.id === selectedAoiId) ?? null;
  const zoomToBounds = useMapActions((s) => s.zoomToBounds);
  const aoiBounds = selectedAoi ? aoiBoundsFlat(selectedAoi.geom) : null;

  // Operating-AOI context — the workbench.
  const activeOperatingAoiId = useOperatingAoiContext((s) => s.activeOperatingAoiId);
  const operatingAois = useOperatingAoiContext((s) => s.operatingAois);
  const activeOperatingAoi =
    operatingAois.find((a) => a.id === activeOperatingAoiId) ?? null;
  const activeOperatingAoiName = activeOperatingAoi?.name ?? null;
  // bounds come from the list endpoint as a flat [w, s, e, n] array.
  const operatingAoiBounds = activeOperatingAoi?.bounds ?? null;

  const [aoiMenuOpen, setAoiMenuOpen] = useState(false);
  const headerRef = useRef(null);

  // Close the AOI dropdown on an outside click.
  useEffect(() => {
    if (!aoiMenuOpen) return;
    function onOutside(e) {
      if (headerRef.current && !headerRef.current.contains(e.target)) {
        setAoiMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", onOutside);
    return () => document.removeEventListener("mousedown", onOutside);
  }, [aoiMenuOpen]);

  return (
    <div
      ref={headerRef}
      className="flex-shrink-0 relative px-2.5 py-2 border-b border-slate-800 bg-bg-softer"
    >
      {activeOperatingAoiId ? (
        <>
          {/* PRIMARY — operating AOI: orange dot + name + zoomTo */}
          <div className="flex items-center gap-2 min-w-0">
            <span
              className="w-1.5 h-1.5 rounded-full flex-shrink-0"
              style={{ background: OPERATING_AOI_COLOR }}
            />
            <span className="flex-1 min-w-0 text-[11px] text-gray-200 truncate">
              {activeOperatingAoiName ?? "Operating AOI"}
            </span>
            {operatingAoiBounds && (
              <button
                type="button"
                onClick={() => zoomToBounds(operatingAoiBounds)}
                title="Zoom to operating AOI"
                className="flex-shrink-0 p-1 rounded text-fg-secondary hover:text-accent-action hover:bg-accent-action/10 transition"
              >
                <LocateFixed size={11} />
              </button>
            )}
          </div>

          {/* SECONDARY — user AOI focus. Clickable to open the picker
              dropdown; muted placeholder when nothing's selected. */}
          {selectedAoi ? (
            <div className="mt-1 flex items-center gap-2 min-w-0">
              <span className="w-1.5 h-1.5 rounded-full bg-accent-action flex-shrink-0" />
              <button
                type="button"
                onClick={() => setAoiMenuOpen((v) => !v)}
                aria-expanded={aoiMenuOpen}
                className="flex-1 min-w-0 text-left text-[11px] text-gray-300 hover:text-gray-100 transition flex items-center gap-1.5 min-w-0"
                title="Change focus AOI"
              >
                <span className="flex-1 min-w-0 truncate">{selectedAoi.name}</span>
                <ChevronDown size={11} className="text-fg-secondary flex-shrink-0" />
              </button>
              {aoiBounds && (
                <button
                  type="button"
                  onClick={() => zoomToBounds(aoiBounds)}
                  title="Zoom to focus AOI"
                  className="flex-shrink-0 p-1 rounded text-fg-secondary hover:text-accent-action hover:bg-accent-action/10 transition"
                >
                  <LocateFixed size={11} />
                </button>
              )}
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setAoiMenuOpen((v) => !v)}
              aria-expanded={aoiMenuOpen}
              className="mt-1 flex items-center gap-2 min-w-0 w-full text-left text-[11px] text-fg-secondary hover:text-gray-300 transition"
              title="Pick a focus AOI"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-slate-700 flex-shrink-0" />
              <span className="flex-1 min-w-0 truncate">No focus selected</span>
              <ChevronDown size={11} className="text-fg-secondary flex-shrink-0" />
            </button>
          )}
        </>
      ) : (
        /* No operating AOI — yellow nudge alone. No AOI affordances. */
        <div className="text-[11px] text-amber-400/80">
          Select an operating AOI to focus your workspace.
        </div>
      )}

      {aoiMenuOpen && (
        <div className="absolute left-0 right-0 top-full mt-1 z-50 max-h-64 overflow-y-auto rounded-md border border-slate-700 bg-bg-softer shadow-xl shadow-black/50 py-1">
          {aois.length === 0 && (
            <div className="px-3 py-2 text-[10px] text-fg-secondary">
              No AOIs in this project.
            </div>
          )}
          {aois.map((aoi) => {
            const active = aoi.id === selectedAoiId;
            return (
              <button
                key={aoi.id}
                type="button"
                onClick={() => { setSelectedAoiForMap(aoi.id); setAoiMenuOpen(false); }}
                className={cx(
                  "w-full flex items-center gap-2 px-3 py-1.5 text-left text-[11px] transition",
                  active
                    ? "text-accent-action"
                    : "text-gray-300 hover:bg-slate-700 hover:text-white"
                )}
              >
                <Hexagon size={11} className="flex-shrink-0" />
                <span className="flex-1 truncate">{aoi.name}</span>
                {active && <Check size={11} className="flex-shrink-0" />}
              </button>
            );
          })}
          {selectedAoiId && (
            <>
              <div className="my-1 border-t border-slate-700/60" />
              <button
                type="button"
                onClick={() => { setSelectedAoiForMap(null); setAoiMenuOpen(false); }}
                className="w-full px-3 py-1.5 text-left text-[10px] text-fg-secondary hover:bg-slate-700 hover:text-gray-300 transition"
              >
                Clear focus AOI
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
